import React, { useState, useEffect, useRef, createContext, useContext } from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { LogOut, Settings, Moon, Sun, BookOpen, Bell, ChevronDown, User, Eye, EyeOff } from 'lucide-react';
import { useAuthStore, useThemeStore, usePrivacidadStore } from '../store/auth';
import api from '../services/api';
import { MASCARA } from './Monto';
import { activable, FOCO } from '../utils/a11y';
import { enmascararMontosEnTexto } from '../utils/formatters';
import Logo from './Logo';

const CloseCtx = createContext(() => {});

function Dropdown({ label, icon, children, align = 'left' }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const location = useLocation();

  useEffect(() => { setOpen(false); }, [location.pathname]);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        aria-label={label || undefined} aria-expanded={open} aria-haspopup="true"
        className="flex items-center justify-center gap-1 min-h-[44px] min-w-[44px] px-2 sm:px-3 py-2 rounded-lg hover:bg-white/10 transition text-sm font-medium whitespace-nowrap select-none"
      >
        {icon && <span aria-hidden="true" className="text-base">{icon}</span>}
        {label && <span className={icon ? "hidden sm:inline max-w-[10rem] truncate" : ""}>{label}</span>}
        <ChevronDown size={13} className={`transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <CloseCtx.Provider value={() => setOpen(false)}>
          <div
            className="absolute top-[calc(100%+4px)] min-w-[210px] bg-white dark:bg-slate-800 text-gray-900 dark:text-white rounded-xl shadow-2xl border border-gray-100 dark:border-slate-700 py-1 overflow-hidden"
            style={{ zIndex: 9999, [align === 'right' ? 'right' : 'left']: 0 }}
          >
            {children}
          </div>
        </CloseCtx.Provider>
      )}
    </div>
  );
}

function DropItem({ to, onClick, children }) {
  const close = useContext(CloseCtx);
  const cls = "flex items-center gap-2 px-4 py-3 text-sm hover:bg-gray-50 dark:hover:bg-slate-700 transition w-full text-left";

  if (to) return (
    <Link to={to} className={cls} onClick={close}>
      {children}
    </Link>
  );
  return (
    <button className={cls} onClick={() => { close(); onClick?.(); }}>
      {children}
    </button>
  );
}

function Divider() {
  return <div className="border-t border-gray-100 dark:border-slate-700 my-1" />;
}

export default function Navbar() {
  const { user, logout }          = useAuthStore();
  const { isDarkMode, toggleTheme } = useThemeStore();
  const { modoPrivado, togglePrivacidad } = usePrivacidadStore();
  const navigate                  = useNavigate();
  const location                  = useLocation();

  const [mobileOpen, setMobileOpen] = useState(false);
  const hamburguesaRef = useRef(null);
  // Escape cierra el menú móvil y devuelve el foco a la hamburguesa
  useEffect(() => {
    if (!mobileOpen) return undefined;
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      setMobileOpen(false);
      hamburguesaRef.current?.focus();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [mobileOpen]);
  const [notifOpen, setNotifOpen]   = useState(false);
  const [notifs, setNotifs]         = useState([]);
  const [pendientes, setPendientes] = useState(0);
  const [revelados, setRevelados]   = useState(new Set());
  const notifRef                    = useRef(null);
  const panelRef                    = useRef(null);

  // Reseteado al apagar/prender el modo, igual que <Monto>.
  useEffect(() => { setRevelados(new Set()); }, [modoPrivado]);
  const toggleRevelado = (id) => setRevelados(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  // Cierra menú móvil al navegar
  useEffect(() => { setMobileOpen(false); setNotifOpen(false); }, [location.pathname]);

  useEffect(() => {
    cargarNotifs();
    const iv = setInterval(cargarNotifs, 60000);
    return () => clearInterval(iv);
  }, []);

  useEffect(() => {
    if (!notifOpen) return;
    const handler = (e) => {
      const inBell  = notifRef.current  && notifRef.current.contains(e.target);
      const inPanel = panelRef.current  && panelRef.current.contains(e.target);
      if (!inBell && !inPanel) setNotifOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [notifOpen]);

  const cargarNotifs = async () => {
    try {
      const res = await api.get('/notificaciones?no_leidas=1&limit=10');
      setNotifs(res.data?.notificaciones || []);
      setPendientes(res.data?.pendientes || 0);
    } catch { /* silencioso */ }
  };

  const marcarLeida = async (id) => {
    try {
      await api.put(`/notificaciones/${id}/leer`);
      setNotifs(n => n.filter(x => x.id !== id));
      setPendientes(p => Math.max(0, p - 1));
    } catch { /* silencioso */ }
  };

  const marcarTodas = async () => {
    try {
      await api.put('/notificaciones/leer-todas');
      setNotifs([]); setPendientes(0);
    } catch { /* silencioso */ }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <>
      <nav className="sticky top-0 bg-gradient-to-r from-indigo-700 to-indigo-600 dark:from-slate-900 dark:to-slate-800 text-white shadow-lg" style={{ zIndex: 1000 }}>
        <div className="px-4 sm:px-6">
          <div className="flex items-center h-14 gap-1">

            {/* Logo */}
            <Link to="/dashboard" aria-label="MONIMEX, ir al panel" className="flex items-center min-h-[44px] mr-3 hover:opacity-90 transition shrink-0">
              <Logo tono="claro" iconClassName="h-7 w-7" textClassName="text-lg hidden sm:inline lg:hidden xl:inline" />
            </Link>

            {/* Desktop nav */}
            <div className="hidden lg:flex items-center gap-0.5 flex-1">

              <Link to="/dashboard"
                className="px-3 py-2 rounded-lg hover:bg-white/10 transition text-sm font-medium whitespace-nowrap">
                📊 Panel
              </Link>

              <Link to="/asistente"
                className="px-3 py-2 rounded-lg hover:bg-white/10 transition text-sm font-medium whitespace-nowrap">
                🤖 Asistente
              </Link>

              <Dropdown label="Transacciones" icon="💵">
                <DropItem to="/ingresos">➕ Nuevo ingreso</DropItem>
                <DropItem to="/gastos">➖ Nuevo gasto</DropItem>
                <DropItem to="/transferencias">🔄 Transferencias</DropItem>
                <DropItem to="/compartidos">🤝 Compartidos</DropItem>
                <DropItem to="/proyectos">💼 Proyectos freelance</DropItem>
                <DropItem to="/ingresos-automaticos">🔁 Ingresos automáticos</DropItem>
                <Divider />
                <DropItem to="/importar">📥 Importar estados de cuenta</DropItem>
                <DropItem to="/cfdi">🧾 Importar CFDI</DropItem>
                <DropItem to="/exportar">📤 Exportar datos</DropItem>
              </Dropdown>

              <Dropdown label="Mi dinero" icon="📊">
                <DropItem to="/cuentas">🏦 Cuentas bancarias</DropItem>
                <DropItem to="/balance">📈 Balance</DropItem>
                <DropItem to="/inversiones">📈 Inversiones</DropItem>
                <Divider />
                <DropItem to="/presupuestos">📋 Presupuestos</DropItem>
                <DropItem to="/metas">🎯 Metas</DropItem>
                <DropItem to="/insignias">🏅 Insignias</DropItem>
                <DropItem to="/impuestos">🧾 Impuestos</DropItem>
                <DropItem to="/reportes">📄 Reportes</DropItem>
                <Divider />
                <DropItem to="/suscripciones">💳 Pagos programados</DropItem>
                <DropItem to="/deudas">🏦 Deudas</DropItem>
              </Dropdown>


              <Dropdown label="Más" icon="⚙️" align="right">
                <DropItem to="/categorias">🏷️ Categorías</DropItem>
                <DropItem to="/reglas">🤖 Reglas auto-cat.</DropItem>
                <DropItem to="/espacios">👥 Espacios compartidos</DropItem>
                <DropItem to="/tarjetas-credito">💳 Tarjetas</DropItem>
                <DropItem to="/calendario">📅 Calendario</DropItem>
                <DropItem to="/calculadora">🧮 Calculadora</DropItem>
                <DropItem to="/patrimonio">💎 Patrimonio neto</DropItem>
                <DropItem to="/busqueda">🔍 Búsqueda</DropItem>
                {user?.rol === 'admin' && <DropItem to="/documentacion">📚 Documentación</DropItem>}
              </Dropdown>
            </div>

            {/* Right side */}
            <div className="flex items-center gap-1 ml-auto">

              {/* Privacidad */}
              <button type="button" onClick={togglePrivacidad} title={modoPrivado ? 'Mostrar cantidades' : 'Ocultar cantidades'}
                aria-label={modoPrivado ? 'Mostrar cantidades' : 'Ocultar cantidades'} aria-pressed={modoPrivado}
                className="p-2 min-w-[44px] min-h-[44px] flex items-center justify-center rounded-lg hover:bg-white/10 transition">
                {modoPrivado ? <EyeOff size={18} aria-hidden="true" /> : <Eye size={18} aria-hidden="true" />}
              </button>

              {/* Dark mode */}
              <button type="button" onClick={toggleTheme} title={isDarkMode ? 'Modo claro' : 'Modo oscuro'}
                aria-label={isDarkMode ? 'Activar modo claro' : 'Activar modo oscuro'}
                className="p-2 min-w-[44px] min-h-[44px] flex items-center justify-center rounded-lg hover:bg-white/10 transition">
                {isDarkMode ? <Sun size={18} aria-hidden="true" /> : <Moon size={18} aria-hidden="true" />}
              </button>

              {/* Notificaciones */}
              <div ref={notifRef}>
                <button type="button" onClick={() => setNotifOpen(o => !o)}
                  aria-label={pendientes > 0 ? `Notificaciones, ${pendientes} sin leer` : 'Notificaciones'}
                  aria-expanded={notifOpen} aria-haspopup="true"
                  className="p-2 min-w-[44px] min-h-[44px] flex items-center justify-center rounded-lg hover:bg-white/10 transition relative">
                  <Bell size={18} aria-hidden="true" />
                  {pendientes > 0 && (
                    <span aria-hidden="true" className="absolute top-1 right-1 w-4 h-4 bg-red-600 rounded-full text-[10px] flex items-center justify-center font-bold leading-none">
                      {pendientes > 9 ? '9+' : pendientes}
                    </span>
                  )}
                </button>
              </div>
              {notifOpen && createPortal(
                <div
                  ref={panelRef}
                  className="bg-white dark:bg-slate-800 text-gray-900 dark:text-white rounded-xl shadow-2xl border border-gray-100 dark:border-slate-700 flex flex-col overflow-hidden"
                  style={{
                    position: 'fixed',
                    top: '60px',
                    right: '6px',
                    left: 'max(6px, calc(100vw - 352px))',
                    zIndex: 99999,
                    maxHeight: 'min(480px, calc(100dvh - 70px))',
                  }}
                >
                  {/* Header */}
                  <div className="flex items-center justify-between px-4 py-3 border-b dark:border-slate-700 shrink-0">
                    <span className="font-semibold text-sm">Notificaciones</span>
                    {pendientes > 0 && (
                      <button onClick={marcarTodas} className="min-h-[44px] px-2 text-xs text-blue-500 dark:text-blue-400 hover:underline">
                        Marcar todas leídas
                      </button>
                    )}
                  </div>
                  {/* List */}
                  <div className="overflow-y-auto flex-1">
                    {notifs.length === 0 ? (
                      <div className="flex flex-col items-center justify-center py-10 gap-2">
                        <Bell size={28} className="text-gray-300 dark:text-gray-600" />
                        <p className="text-sm text-gray-500 dark:text-gray-400">Sin notificaciones nuevas</p>
                      </div>
                    ) : notifs.map(n => {
                      const oculto = modoPrivado && !revelados.has(n.id);
                      const titulo  = oculto ? enmascararMontosEnTexto(n.titulo, MASCARA)  : n.titulo;
                      const mensaje = oculto ? enmascararMontosEnTexto(n.mensaje, MASCARA) : n.mensaje;
                      return (
                      <div
                        key={n.id}
                        {...activable(modoPrivado ? () => toggleRevelado(n.id) : undefined, { pressed: modoPrivado ? revelados.has(n.id) : undefined })}
                        title={modoPrivado ? (revelados.has(n.id) ? 'Clic para ocultar montos' : 'Clic para ver montos') : undefined}
                        className={`px-4 py-3 hover:bg-gray-50 dark:hover:bg-slate-700/60 flex gap-3 border-b dark:border-slate-700 last:border-0 transition-colors ${modoPrivado ? `cursor-pointer ${FOCO}` : ''}`}
                      >
                        <span className="text-xl shrink-0 mt-0.5">
                          {n.tipo === 'warning' ? '⚠️' : n.tipo === 'success' ? '✅' : n.tipo === 'error' ? '🚨' : 'ℹ️'}
                        </span>
                        <div className="flex-1 min-w-0">
                          <p className={`text-sm font-semibold leading-snug line-clamp-2 ${oculto ? 'select-none' : ''}`}>{titulo}</p>
                          {mensaje && <p className={`text-xs text-gray-500 dark:text-gray-400 mt-0.5 line-clamp-2 ${oculto ? 'select-none' : ''}`}>{mensaje}</p>}
                          {n.enlace && (
                            <button
                              onClick={(e) => { e.stopPropagation(); marcarLeida(n.id); setNotifOpen(false); navigate(n.enlace); }}
                              className="text-xs font-semibold text-blue-600 dark:text-blue-400 hover:underline mt-1"
                            >Ver →</button>
                          )}
                          {n.creada_en && (
                            <p className="text-[10px] text-gray-400 mt-1">
                              {/* monto-ok: es una fecha, no un monto */}
                              {new Date(n.creada_en).toLocaleString('es-MX', { dateStyle: 'short', timeStyle: 'short' })}
                            </p>
                          )}
                        </div>
                        <button
                          onClick={(e) => { e.stopPropagation(); marcarLeida(n.id); }}
                          className="text-gray-300 hover:text-red-400 dark:hover:text-red-400 shrink-0 p-1 -mr-1 rounded transition-colors"
                          title="Marcar como leída"
                          aria-label="Marcar como leída"
                        >✕</button>
                      </div>
                      );
                    })}
                  </div>
                </div>,
                document.body
              )}

              {/* Usuario */}
              <Dropdown label={user?.usuario} icon={<User size={14} />} align="right">
                <div className="px-4 py-2.5 border-b dark:border-slate-700 pointer-events-none">
                  <p className="text-xs text-gray-400">Conectado como</p>
                  <p className="font-semibold text-sm">{user?.usuario}</p>
                  <span className={`inline-block text-xs px-1.5 py-0.5 rounded mt-0.5 font-medium ${
                    user?.plan === 'pro' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300' :
                    'bg-gray-100 text-gray-600 dark:bg-slate-700 dark:text-gray-300'
                  }`}>{user?.plan || 'free'}</span>
                </div>
                <DropItem to="/perfil"><Settings size={13} /> Perfil</DropItem>
                <DropItem to="/planes">⭐ Mi plan</DropItem>
                {user?.rol === 'admin' && <DropItem to="/documentacion"><BookOpen size={13} /> Documentación</DropItem>}
                {user?.rol === 'admin' && (
                  <>
                    <Divider />
                    <DropItem to="/admin">🛡️ Administración</DropItem>
                  </>
                )}
                <Divider />
                <DropItem onClick={handleLogout}>
                  <LogOut size={13} className="text-red-500 dark:text-red-400" />
                  <span className="text-red-500 dark:text-red-400">Cerrar sesión</span>
                </DropItem>
              </Dropdown>

              {/* Hamburger móvil */}
              <button ref={hamburguesaRef} type="button" onClick={() => setMobileOpen(o => !o)}
                aria-expanded={mobileOpen} aria-controls="menu-movil" aria-label="Abrir menú"
                className="lg:hidden p-2 min-w-[44px] min-h-[44px] flex items-center justify-center rounded-lg hover:bg-white/10 transition">
                <div aria-hidden="true" className="w-5 flex flex-col gap-1.5">
                  <span className={`block h-0.5 bg-white rounded transition-all duration-200 origin-center ${mobileOpen ? 'rotate-45 translate-y-2' : ''}`} />
                  <span className={`block h-0.5 bg-white rounded transition-all duration-200 ${mobileOpen ? 'opacity-0 scale-x-0' : ''}`} />
                  <span className={`block h-0.5 bg-white rounded transition-all duration-200 origin-center ${mobileOpen ? '-rotate-45 -translate-y-2' : ''}`} />
                </div>
              </button>
            </div>
          </div>
        </div>

        {/* Mobile menu */}
        {mobileOpen && (
          <div id="menu-movil" className="lg:hidden border-t border-white/20 pb-3 pt-1 px-2">
            {/* Notificaciones en móvil */}
            {pendientes > 0 && (
              <button
                onClick={() => { setMobileOpen(false); setNotifOpen(true); }}
                className="w-full flex items-center gap-2 px-3 py-3 rounded-lg hover:bg-white/10 transition text-sm mb-1 text-left"
              >
                <Bell size={16} />
                <span>Notificaciones</span>
                <span className="ml-auto bg-red-600 text-white text-[10px] font-bold w-5 h-5 rounded-full flex items-center justify-center">
                  {pendientes > 9 ? '9+' : pendientes}
                </span>
              </button>
            )}
            <div className="grid grid-cols-2 gap-0.5">
              {[
                ['/dashboard',            '📊 Panel'],
                ['/asistente',            '🤖 Asistente'],
                ['/ingresos',             '➕ Ingreso'],
                ['/gastos',               '➖ Gasto'],
                ['/ingresos-automaticos', '🔁 Automáticos'],
                ['/balance',              '📈 Balance'],
                ['/calendario',           '📅 Calendario'],
                ['/patrimonio',           '💎 Patrimonio'],
                ['/inversiones',          '📈 Inversiones'],
                ['/presupuestos',         '📋 Presupuestos'],
                ['/metas',                '🎯 Metas'],
                ['/insignias',            '🏅 Insignias'],
                ['/calculadora',          '🧮 Calculadora'],
                ['/impuestos',            '🧾 Impuestos'],
                ['/reportes',             '📄 Reportes'],
                ['/suscripciones',        '💳 Pagos prog.'],
                ['/transferencias',       '🔄 Transferencias'],
                ['/compartidos',          '🤝 Compartidos'],
                ['/proyectos',            '💼 Proyectos'],
                ['/deudas',               '🏦 Deudas'],
                ['/categorias',           '🏷️ Categorías'],
                ['/reglas',               '🤖 Reglas auto-cat.'],
                ['/espacios',             '👥 Espacios'],
                ['/tarjetas-credito',     '💳 Tarjetas'],
                ['/cuentas',              '🏦 Cuentas'],
                ['/busqueda',             '🔍 Búsqueda'],
                ['/importar',             '📥 Importar'],
                ['/cfdi',                 '🧾 CFDI'],
                ['/exportar',             '📤 Exportar'],
                ['/perfil',               '⚙️ Perfil'],
                ['/planes',               '⭐ Mi plan'],
              ].map(([to, label]) => (
                <Link key={to} to={to}
                  className="block px-3 py-3 rounded-lg hover:bg-white/10 transition text-sm">
                  {label}
                </Link>
              ))}
              {user?.rol === 'admin' && (
                <Link to="/admin" className="block px-3 py-3 rounded-lg hover:bg-white/10 transition text-sm">
                  🛡️ Admin
                </Link>
              )}
            </div>
            <div className="border-t border-white/20 mt-2 pt-2 px-1">
              <button onClick={handleLogout}
                className="w-full text-left px-3 py-3 rounded-lg hover:bg-red-500/20 transition text-sm text-red-200">
                🚪 Cerrar sesión
              </button>
            </div>
          </div>
        )}
      </nav>
    </>
  );
}
